import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BuildingsIcon, CaretDownIcon, InfinityIcon } from "@phosphor-icons/react";

const ROLE_OPTIONS = ["admin", "manager", "front_desk", "housekeeping", "restaurant_manager", "service"];

const currentRoles = (u, properties) => {
  const known = new Set(properties.map((p) => p.id));
  const pr = {};
  Object.entries(u.property_roles || {}).forEach(([pid, r]) => { if (known.has(pid)) pr[pid] = r; });
  (u.property_ids || []).forEach((pid) => { if (known.has(pid) && !pr[pid]) pr[pid] = u.role; });
  return pr;
};

export const PropertyRoles = ({ user: staffUser, properties, roleLabel, onSave, disabled, allowAdmin }) => {
  const [open, setOpen] = useState(false);
  const [roles, setRoles] = useState(() => currentRoles(staffUser, properties));
  useEffect(() => { if (open) setRoles(currentRoles(staffUser, properties)); }, [open, staffUser, properties]);
  const n = Object.keys(currentRoles(staffUser, properties)).length;
  const options = ROLE_OPTIONS.filter((r) => allowAdmin || r !== "admin");
  const label = n === 0
    ? <span className="text-muted-foreground flex items-center gap-1"><InfinityIcon size={12} /> All · {roleLabel[staffUser.role]}</span>
    : `${n} of ${properties.length} propert${properties.length === 1 ? "y" : "ies"}`;
  if (disabled) return <div className="text-xs" data-testid={`staff-assign-label-${staffUser.id}`}>{label}</div>;

  const setOne = (pid, v) => setRoles((r) => {
    const next = { ...r };
    if (v === "none") delete next[pid]; else next[pid] = v;
    return next;
  });
  const save = async () => { await onSave(roles); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs justify-between min-w-[150px]" data-testid={`staff-assign-${staffUser.id}`}>
          <span className="flex items-center gap-1"><BuildingsIcon size={12} /> {label}</span>
          <CaretDownIcon size={10} className="ml-1 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="end">
        <p className="label-overline mb-1">Property access &amp; role</p>
        <p className="text-xs text-muted-foreground mb-3">Pick a role for each property this person works at. Leave every property on <strong>No access</strong> to let them use their main role at all properties.</p>
        <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
          {properties.map((p) => (
            <div key={p.id} className="flex items-center gap-2 text-sm" data-testid={`staff-assign-prop-${p.id}`}>
              <span className="flex-1 truncate">{p.name}</span>
              <Select value={roles[p.id] || "none"} onValueChange={(v) => setOne(p.id, v)}>
                <SelectTrigger className="w-44 h-8 text-xs" data-testid={`staff-prop-role-${staffUser.id}-${p.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No access</SelectItem>
                  {options.map((r) => <SelectItem key={r} value={r}>{roleLabel[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
          {properties.length === 0 && <p className="text-xs italic text-muted-foreground">No properties yet.</p>}
        </div>
        <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-border">
          <Button size="sm" variant="ghost" onClick={() => setRoles({})} data-testid={`staff-assign-clear-${staffUser.id}`}>Clear</Button>
          <Button size="sm" onClick={save} data-testid={`staff-assign-save-${staffUser.id}`}>Save</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
